import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { filters } from "components/Common/constants"
import Switch from "components/Shared/Switch"
import { showAlert } from "store/reducers/alertReducer"
import DeleteModal from "components/Common/DeleteModal"
import { getAllBranchRatings } from "store/actions/ratingsAction"
import { getAllBranchs } from "store/actions/usersAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { EXPORT_DATA } from "helpers/url_helper"
import { pdfDownload } from "helpers/api_helper"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"

const BranchRatings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Branch Ratings `)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const branchIdParam = searchParams.get("branchId")
  const minDate = searchParams.get("minDate")
  const maxDate = searchParams.get("maxDate")

  const {
    loading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    isLoading,
    branchRatings,
  } = useSelector(state => state.Ratings)

  const { branchsList } = useSelector(state => state.Users)

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
    branchId: branchIdParam
      ? branchIdParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })
  const [selectedId, setSelectedId] = useState(null)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })
  const flatpickrRef = useRef(null)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      27,
      "rating_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  useEffect(() => {
    fetchBranchRating()
    fetchAllBranchs()
  }, [])

  const fetchBranchRating = () => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      branchIds: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllBranchRatings(payload))
    setIsApiCalled(true)
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const fetchBranchRatings = (page = 1) => {
    const { sortOrder, branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }

    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      branchIds: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllBranchRatings(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/branch-ratings?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    fetchBranchRatings(1)
  }

  const onPageChange = page => {
    fetchBranchRatings(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 0 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branches"),
    },
  ]

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      orderBy: 1,
      keyword: "",
      branchIds: [],
      dateRange: {},
    }
    dispatch(getAllBranchRatings(payload))
    navigate(`/branch-ratings?page=1`)
    setSearchTerm("")
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
    setSelectedFilters({
      sortOrder: 1,
      branchId: [],
    })
  }

  const exportToCSV = async () => {
    const { branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      keyword: searchTerm || "",
      type: "RATINGS",
      exportType: "csv",
      branchId: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Branch Ratings"))
    }
  }

  const handleExcelDownload = async () => {
    const { branchId } = selectedFilters
    const branchIds = branchId?.map(item => item.value) || []

    const payload = {
      keyword: searchTerm || "",
      type: "RATINGS",
      exportType: "xlsx",
      branchId: branchIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Branch Ratings"))
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Branch Ratings")}
                isExportData={loading ? false : true}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Email & Phone Number")}
                  colSize={4}
                />
                <div>
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : branchRatings.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No branch rating found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Branch Name")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Email")}</th>
                          <th>{t("Phone Number")}</th>
                          <th>{t("Dob")}</th>
                          <th>{t("Platform")}</th>
                          <th>{t("Feedback")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {branchRatings.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(
                                `/branch-ratings/branch-rating-details?id=${item._id}`
                              )
                            }}
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>
                              {language === "sa"
                                ? item?.branchNameAr
                                : item?.branchName}
                            </td>
                            <td>{item?.userInfo?.name}</td>
                            <td>{item?.userInfo?.email}</td>
                            <td>
                              {item?.userInfo?.phoneCode ||
                              item?.userInfo?.phoneNumber ? (
                                <span
                                  dir="ltr"
                                  style={{ unicodeBidi: "bidi-override" }}
                                >
                                  {`${item?.userInfo?.phoneCode || ""} ${
                                    item?.userInfo?.phoneNumber || ""
                                  }`.trim()}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{item?.userInfo?.dob || "-"}</td>
                            <td>{t(item?.platform) || "-"}</td>
                            <td>{item?.feedback || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && branchRatings.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={branchRatings}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BranchRatings
